import { useEffect, useState } from "react";
import { Briefcase, Plus, Copy, Loader2, AlertTriangle, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { mxn, fdate, fdatetime } from "@/lib/format";
import { toast } from "sonner";

// ============================================================================
// PaquetesContador — ago-2026 · Fase 3. Canal de venta por contador: paga por
// TRANSFERENCIA un paquete de N espacios (mínimo 5) y la plataforma genera N
// códigos de un solo uso que él reparte entre sus clientes.
// El contador NO es usuario: aquí solo se guardan sus datos de contacto y la
// constancia del pago. Espacios y vigencia son INFORMATIVOS: no bloquean ni
// suspenden nada — el super_admin cobra la renovación manualmente.
// ============================================================================

const PRECIO_ESPACIO_SUGERIDO = 300;

const hoyISO = () => new Date().toISOString().slice(0, 10);
const enUnAnio = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

const FORM_INICIAL = {
  contador_nombre: "", contador_rfc: "", contador_correo: "", contador_telefono: "",
  espacios_totales: 5, monto_pagado: 5 * PRECIO_ESPACIO_SUGERIDO,
  fecha_pago: hoyISO(), fecha_vencimiento: enUnAnio(), referencia_pago: "", notas: "",
};

const Campo = ({ id, label, hint, children }) => (
  <div>
    <Label htmlFor={id} className="mb-1 block text-xs font-medium text-slate-700">
      {label} {hint && <span className="font-normal text-slate-400">— {hint}</span>}
    </Label>
    {children}
  </div>
);

export const PaquetesContador = () => {
  const [paquetes, setPaquetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState(FORM_INICIAL);
  const [guardando, setGuardando] = useState(false);
  const [nuevos, setNuevos] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/paquetes-contador");
      setPaquetes(data || []);
    } catch (e) {
      toast.error("No se pudieron cargar los paquetes de contador");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "espacios_totales") {
      const n = parseInt(v, 10);
      if (!Number.isNaN(n) && n > 0) next.monto_pagado = n * PRECIO_ESPACIO_SUGERIDO;
    }
    return next;
  });

  const crear = async () => {
    setGuardando(true);
    try {
      const { data } = await api.post("/admin/paquetes-contador", {
        ...form,
        espacios_totales: parseInt(form.espacios_totales, 10) || 0,
        monto_pagado: parseFloat(form.monto_pagado) || 0,
      });
      setNuevos(data.codigos.map((c) => c.codigo));
      setForm(FORM_INICIAL);
      setAbierto(false);
      toast.success(`Paquete registrado · ${data.codigos.length} códigos generados`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el paquete");
    } finally { setGuardando(false); }
  };

  const copiar = (texto, msg) => {
    navigator.clipboard?.writeText(texto);
    toast.success(msg);
  };

  return (
    <section data-testid="paquetes-contador-section"
             className="rounded-xl border border-indigo-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100">
            <Briefcase className="h-4.5 w-4.5 text-indigo-700" />
          </span>
          <div>
            <p className="font-heading text-sm font-semibold text-slate-900">
              Paquetes de contador (pagados por transferencia)
            </p>
            <p className="text-xs text-slate-500">
              El contador compra un paquete de espacios (mínimo 5) y reparte un código a cada
              cliente. A diferencia de una cortesía normal, aquí queda constancia del pago.
              El contador no es usuario de la plataforma: no tiene cuenta ni acceso.
            </p>
          </div>
        </div>
        <Button data-testid="pc-form-toggle" size="sm" variant={abierto ? "ghost" : "outline"}
                onClick={() => setAbierto((v) => !v)}>
          <Plus className="mr-1.5 h-4 w-4" /> {abierto ? "Cancelar" : "Registrar paquete"}
        </Button>
      </div>

      {abierto && (
        <div data-testid="pc-form" className="mb-4 grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Campo id="pc-nombre" label="Nombre del contador">
            <Input id="pc-nombre" data-testid="pc-input-nombre" value={form.contador_nombre}
                   onChange={(e) => set("contador_nombre", e.target.value)}
                   placeholder="C.P. Nombre Apellido" />
          </Campo>
          <Campo id="pc-rfc" label="RFC del contador">
            <Input id="pc-rfc" data-testid="pc-input-rfc" value={form.contador_rfc}
                   onChange={(e) => set("contador_rfc", e.target.value.toUpperCase())}
                   className="font-mono" placeholder="XAXX010101000" />
          </Campo>
          <Campo id="pc-correo" label="Correo">
            <Input id="pc-correo" data-testid="pc-input-correo" value={form.contador_correo}
                   onChange={(e) => set("contador_correo", e.target.value)}
                   placeholder="contador@despacho.mx" />
          </Campo>
          <Campo id="pc-tel" label="Teléfono">
            <Input id="pc-tel" data-testid="pc-input-telefono" value={form.contador_telefono}
                   onChange={(e) => set("contador_telefono", e.target.value)}
                   placeholder="222 123 4567" />
          </Campo>
          <Campo id="pc-espacios" label="Espacios contratados" hint="mínimo 5">
            <Input id="pc-espacios" data-testid="pc-input-espacios" type="number" min={5}
                   value={form.espacios_totales}
                   onChange={(e) => set("espacios_totales", e.target.value)} />
          </Campo>
          <Campo id="pc-monto" label="Monto pagado (MXN)" hint={`sugerido $${PRECIO_ESPACIO_SUGERIDO}/espacio`}>
            <Input id="pc-monto" data-testid="pc-input-monto" type="number" min={1}
                   value={form.monto_pagado}
                   onChange={(e) => set("monto_pagado", e.target.value)} />
          </Campo>
          <Campo id="pc-fpago" label="Fecha de pago">
            <Input id="pc-fpago" data-testid="pc-input-fecha-pago" type="date"
                   value={form.fecha_pago} onChange={(e) => set("fecha_pago", e.target.value)} />
          </Campo>
          <Campo id="pc-fvenc" label="Fecha de vencimiento">
            <Input id="pc-fvenc" data-testid="pc-input-fecha-venc" type="date"
                   value={form.fecha_vencimiento}
                   onChange={(e) => set("fecha_vencimiento", e.target.value)} />
          </Campo>
          <Campo id="pc-ref" label="Referencia de la transferencia" hint="opcional">
            <Input id="pc-ref" data-testid="pc-input-referencia" value={form.referencia_pago}
                   onChange={(e) => set("referencia_pago", e.target.value)}
                   placeholder="SPEI 1234567" />
          </Campo>
          <Campo id="pc-notas" label="Notas" hint="opcional">
            <Input id="pc-notas" data-testid="pc-input-notas" value={form.notas}
                   onChange={(e) => set("notas", e.target.value)} />
          </Campo>
          <div className="flex items-end lg:col-span-2">
            <Button data-testid="pc-crear-btn" onClick={crear} disabled={guardando}
                    className="bg-indigo-700 hover:bg-indigo-800">
              {guardando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                         : <Plus className="mr-1.5 h-4 w-4" />}
              Registrar paquete y generar códigos
            </Button>
          </div>
        </div>
      )}

      {nuevos && (
        <div data-testid="pc-nuevos-codigos"
             className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-emerald-800">
              {nuevos.length} códigos generados — entrégalos al contador:
            </p>
            <Button variant="outline" size="sm" onClick={() => copiar(nuevos.join("\n"), "Códigos copiados")}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-100">
              <Copy className="mr-1.5 h-4 w-4" /> Copiar todos
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {nuevos.map((c) => (
              <span key={c} className="rounded-md border border-emerald-300 bg-white px-2 py-1 font-mono text-sm font-bold tracking-wide text-emerald-900">
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <p className="py-6 text-center text-sm text-slate-400">
          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        </p>
      ) : paquetes.length === 0 ? (
        <p data-testid="pc-vacio" className="py-6 text-center text-sm text-slate-400">
          Aún no hay paquetes de contador registrados.
        </p>
      ) : (
        <div className="space-y-4">
          {paquetes.map((p) => (
            <div key={p.id} data-testid={`pc-card-${p.id}`}
                 className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-heading text-sm font-semibold text-slate-900">
                    {p.contador_nombre} <span className="font-mono text-xs text-slate-500">{p.contador_rfc}</span>
                  </p>
                  <p className="text-xs text-slate-500">
                    {p.contador_correo} · {p.contador_telefono}
                  </p>
                  <p className="mt-1 text-xs text-slate-600" data-testid={`pc-pago-${p.id}`}>
                    Pagado por transferencia: <b>{mxn(p.monto_pagado)}</b> el {fdate(p.fecha_pago)}
                    {p.referencia_pago ? ` · ref. ${p.referencia_pago}` : ""}
                    {" · "}{mxn(p.precio_por_espacio)}/espacio
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span data-testid={`pc-espacios-${p.id}`} data-alerta={p.alerta_espacios ? "si" : "no"}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          p.espacios_disponibles === 0
                            ? "border-red-300 bg-red-100 text-red-800"
                            : p.alerta_espacios
                              ? "border-amber-300 bg-amber-100 text-amber-900"
                              : "border-slate-200 bg-slate-100 text-slate-700"}`}>
                    {p.alerta_espacios && <AlertTriangle className="h-3 w-3" />}
                    {p.espacios_canjeados}/{p.espacios_totales} canjeados ·{" "}
                    {p.espacios_disponibles} disponible{p.espacios_disponibles === 1 ? "" : "s"}
                  </span>
                  <span data-testid={`pc-vigencia-${p.id}`} data-alerta={p.alerta_vigencia ? "si" : "no"}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          p.vencido
                            ? "border-red-300 bg-red-100 text-red-800"
                            : p.alerta_vigencia
                              ? "border-amber-300 bg-amber-100 text-amber-900"
                              : "border-slate-200 bg-slate-100 text-slate-700"}`}>
                    <CalendarClock className="h-3 w-3" />
                    Vence {fdate(p.fecha_vencimiento)}
                    {p.dias_para_vencer !== null && p.dias_para_vencer !== undefined
                      ? p.vencido
                        ? ` · venció hace ${Math.abs(p.dias_para_vencer)} d`
                        : ` · ${p.dias_para_vencer} d`
                      : ""}
                  </span>
                </div>
              </div>

              {(p.alerta_espacios || p.alerta_vigencia) && (
                <p data-testid={`pc-aviso-${p.id}`}
                   className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900">
                  {p.alerta_espacios && "Quedan 1 o 0 espacios: conviene ofrecerle recarga al contador. "}
                  {p.alerta_vigencia && (p.vencido
                    ? "El paquete ya venció: cobra la renovación (no se suspendió nada automáticamente)."
                    : "Vence en 15 días o menos: cobra la renovación (no se suspende nada automáticamente).")}
                </p>
              )}

              <div className="mt-3 overflow-hidden rounded-md border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-left text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Código</th>
                      <th className="px-3 py-2 font-semibold">Estado</th>
                      <th className="px-3 py-2 font-semibold">Entidad que lo canjeó</th>
                      <th className="px-3 py-2 font-semibold">Canjeado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(p.codigos || []).map((c) => (
                      <tr key={c.codigo} data-testid={`pc-codigo-row-${c.codigo}`}>
                        <td className="px-3 py-2">
                          <span className="font-mono font-semibold text-slate-900">{c.codigo}</span>
                          <button onClick={() => copiar(c.codigo, `Código ${c.codigo} copiado`)}
                                  className="ml-2 text-slate-400 hover:text-slate-700" title="Copiar">
                            <Copy className="inline h-3 w-3" />
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          <span data-testid={`pc-codigo-estado-${c.codigo}`}
                                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                  c.estado === "usado" ? "bg-slate-200 text-slate-700"
                                  : c.estado === "desactivado" ? "bg-red-100 text-red-700"
                                  : "bg-emerald-100 text-emerald-800"}`}>
                            {c.estado === "usado" ? "Canjeado"
                              : c.estado === "desactivado" ? "Desactivado" : "Disponible"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {c.usado_por_nombre
                            ? <>{c.usado_por_nombre}{c.usado_por_rfc ? <span className="ml-1 font-mono text-[10px] text-slate-500">{c.usado_por_rfc}</span> : null}</>
                            : "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-500">
                          {c.usado_en ? fdatetime(c.usado_en) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default PaquetesContador;
